import React, { ChangeEvent, FC } from "react";
import styles from "./TextInput.module.css";

interface TextInputProps {
    label?: string;
    description?: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: (value: string) => void;
    placeholder?: string;
    type?: "text" | "password" | "email";
    errorMessage?: string;
    id: string;
    multiline?: boolean;
    height?: string; // Default height for multiline input
    disabled?: boolean; // New disabled prop
}

const TextInput: FC<TextInputProps> = ({
    label,
    description,
    value,
    onChange,
    onBlur,
    placeholder = "",
    type = "text",
    errorMessage = "",
    id,
    multiline = false,
    height = "auto",
    disabled = false, // Default to false
}) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!disabled) {
            onChange(event.target.value);
        }
    };

    const handleBlur = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!disabled && onBlur) {
            onBlur(event.target.value);
        }
    };

    const isValid = !errorMessage;

    return (
        <div className={styles.container}>
            {label && <label htmlFor={id} className={styles.label}>{label}</label>}
            {description && <div className={styles.description}>{description}</div>}
            {multiline ? (
                <textarea
                    id={id}
                    className={`${styles.textInput} ${isValid ? "" : styles.invalid}`}
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    aria-invalid={!isValid}
                    aria-describedby={!isValid ? `${id}-error` : undefined}
                    style={{ height }}
                    disabled={disabled} // Apply the disabled prop
                />
            ) : (
                <input
                    id={id}
                    className={`${styles.textInput} ${isValid ? "" : styles.invalid}`}
                    type={type}
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    aria-invalid={!isValid}
                    aria-describedby={!isValid ? `${id}-error` : undefined}
                    disabled={disabled} // Apply the disabled prop
                />
            )}
            {!isValid && (
                <span id={`${id}-error`} className={styles.errorMessage}>
                    {errorMessage}
                </span>
            )}
        </div>
    );
};

export default TextInput;
